import { CriteoIntegration } from "./_criteoApi";
import {
  flagshipTemplate,
  flagshipProductCardTemplate,
} from "./_flagshipTemplate";

export class CriteoFlagship extends CriteoIntegration {
  #debug = true; // Set to true to enable debug logs

  #selectors = {
    mainPage: "#page .main-content",
    ulList: ".snize-search-results-content.clearfix",
    results: "snize_results",
    flagshipContainer: ".criteo-flagship-container",
    brandImage: ".flagship-brand-image",
    brandCta: ".flagship-brand-cta",
    productsZone: ".criteo-flagship-products",
    productCard: ".flagship-product-card",
    productLink: ".flagship-product-link",
    productImage: ".flagship-product-image",
    productTitle: ".flagship-product-title",
    productPrice: ".flagship-product-price",
    productRating: ".flagship-product-rating",
    energyLabel: ".energy-label-badge",
  };

  #flagshipElement = null;
  #response = null;
  #hasRendered = false;
  #layoutObserver = null;

  static #PLACEMENT_TYPES = {
    MID: "midBranding",
    BOTTOM: "bottomBranding",
    TOP: "topBranding",
  };

  static isEnabled = true;

  /**
   * @param {boolean} debug - Enable/disable debug logs
   */
  constructor(debug = false) {
    super();
    this.#debug = debug;
  }

  /**
   * Set the API response from an external source (to avoid duplicate API calls)
   * @param {object} response
   */
  setResponse(response) {
    this.#response = response;
  }

  /**
   * Centralized logging method
   * @param {string} message
   * @param {any} data
   */
  #log(message, data = null) {
    if (!this.#debug) return;
    if (data !== null) {
      console.log(`[Criteo Flagship] ${message}`, data);
    } else {
      console.log(`[Criteo Flagship] ${message}`);
    }
  }

  /**
   * Wait for Searchanise to render the product grid
   * @returns {Promise<HTMLElement>}
   */
  async #grid() {
    return new Promise((resolve) => {
      let grid = document.querySelector(this.#selectors.ulList);
      if (!grid) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach(({ type, addedNodes }) => {
            if (type === "childList") {
              addedNodes.forEach((node) => {
                if (node.id === this.#selectors.results) {
                  observer.disconnect();
                  resolve(node.querySelector(this.#selectors.ulList));
                }
              });
            }
          });
        });
        observer.observe(document.querySelector(this.#selectors.mainPage), {
          childList: true,
          subtree: true,
        });
      } else {
        resolve(grid);
      }
    });
  }

  /**
   * Generates API params for Flagship request
   * @param {"search-results-page"|"collections"|"products"} template
   * @param {HTMLElement|null} grid
   */
  async #generateParams(template, grid) {
    if (template === "products") {
      return {
        item: window?.product?.variants?.[0]?.sku || window?.product_sku,
        price: (window?.product?.price || 0) / 100,
        "list-price": (window?.product?.price || 0) / 100,
        availability: window?.product?.available ? 1 : 0,
      };
    }

    const skus = Array.from(grid.querySelectorAll("span.snize-sku")).map(
      (span) => span.innerText,
    );

    const [prefix, id] = window.criteoCategory?.split("_") || [];

    return {
      item: skus.join("|"),
      "list-size": skus.length,
      "page-number": 1,
      ...(template === "collections" && { category: `${prefix}_${id}` }),
      ...(template === "search-results-page" && {
        keywords: (() => {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.has("rb_product_type")) {
            return urlParams.get("rb_product_type").toLowerCase();
          }
          return decodeURI(
            window.location.search
              .replace("?q=", "")
              .split("&")[0]
              .replace(/%20/g, " "),
          );
        })(),
      }),
    };
  }

  /**
   * Creates a product card element
   * @param {object} product - Shopify product enriched with Criteo data
   * @returns {HTMLElement}
   */
  #createProductCard(product) {
    const cardContainer = document.createElement("div");
    cardContainer.innerHTML = flagshipProductCardTemplate;
    const card = cardContainer.firstElementChild;

    const price = parseFloat(product.priceRange.minVariantPrice.amount);
    const compareAt = parseFloat(
      product.compareAtPriceRange.minVariantPrice.amount,
    );

    card.querySelector(this.#selectors.productImage).src =
      product.featuredImage?.src;
    card.querySelector(this.#selectors.productImage).alt = product.title;
    card.querySelector(this.#selectors.productTitle).innerText = product.title;

    const productLink = card.querySelector(this.#selectors.productLink);
    productLink.href = product.onlineStoreUrl;
    productLink.title = product.title;

    card.querySelector(this.#selectors.productPrice).innerHTML =
      this.#priceMarkup(price, compareAt);

    // Criteo beacons
    card.dataset.criteoViewBeacon = product.criteo?.OnViewBeacon;
    card.dataset.criteoOnLoadBeacon = product.criteo?.OnLoadBeacon;
    productLink.dataset.criteoOnClickBeacon = product.criteo?.OnClickBeacon;

    // Rating summary Bazaarvoice
    const ratingContainer = card.querySelector(this.#selectors.productRating);
    if (ratingContainer) {
      const bvRoot = ratingContainer.querySelector(
        '[data-bv-show="rating_summary"]',
      );
      if (bvRoot) {
        const sku =
          product.criteo?.ProductId || product.variants?.nodes?.[0]?.sku;
        if (sku) {
          bvRoot.setAttribute("data-bv-product-id", sku);
        }
      }
    }

    // Energy label
    const energyLabelHTML = this.#getEnergyLabel(product.tags);
    const energyLabelContainer = card.querySelector(".product-energy-tag");
    if (energyLabelHTML) {
      energyLabelContainer.innerHTML = energyLabelHTML;
    } else {
      energyLabelContainer.remove();
    }

    return card;
  }

  /**
   * Gets energy label HTML based on product tags
   * @param {string[]} tags
   * @returns {string|null}
   */
  #getEnergyLabel(tags) {
    const energies = ["a", "b", "c", "d", "e", "f", "g"];
    for (const tag of tags) {
      const lowerTag = tag.toLowerCase();
      if (lowerTag.startsWith("energy-")) {
        const energyLetter = lowerTag.split("-")[1];
        if (energies.includes(energyLetter)) {
          return `
            <div class="energy-tag-wrapper energy-${energyLetter}">
              <div class="energy-scale">
                <span class="letterA">A</span>
                <span class="letterG">G</span>
              </div>
              <span class="energy-tag">${energyLetter}</span>
              <span class="energy-shape"></span>
            </div>`;
        }
      }
    }
    return null;
  }

  /**
   * Generates price markup HTML
   * @param {number} price
   * @param {number} compareAt
   * @returns {string}
   */
  #priceMarkup(price, compareAt) {
    const priceFormatted = this._formatPrice(price);
    const compareFormatted = this._formatPrice(compareAt);
    return `
      ${
        compareAt > price
          ? `<span class="flagship-compare-price">${compareFormatted}</span>`
          : ""
      }
      <span class="flagship-regular-price ${
        compareAt > price ? "flagship-sale-price" : ""
      }">${priceFormatted}</span>
      <span class="price-tax">IGIC Incl.</span>`;
  }

  /**
   * Builds the Flagship element with product cards.
   * Does NOT insert into DOM — image src is set after insertion via #setupUpdateImage.
   * @param {object} flagshipData - Criteo placement data
   * @param {HTMLElement|null} grid
   * @returns {Promise<HTMLElement>}
   */
  async #renderFlagship(flagshipData, grid) {
    const { rendering, products, OnViewBeacon, OnLoadBeacon, OnClickBeacon } =
      flagshipData;

    this.#log("Rendering Flagship:", {
      hasRendering: !!rendering,
      productsCount: products?.length,
    });

    const flagshipContainer = document.createElement("li");
    flagshipContainer.id = "criteo-flagship-wrapper";
    flagshipContainer.innerHTML = flagshipTemplate;

    const flagship = flagshipContainer.querySelector(
      this.#selectors.flagshipContainer,
    );
    const brandCta = flagshipContainer.querySelector(this.#selectors.brandCta);

    if (rendering) {
      flagshipContainer.classList.add("criteo-flagship-rendering-format");

      if (rendering.border_color && flagship) {
        flagship.style.borderColor = rendering.border_color;
      }

      if (rendering.optional_footer_redirect_url) {
        brandCta.href = rendering.optional_footer_redirect_url;
        brandCta.dataset.criteoOnClickBeacon = OnClickBeacon;
      } else {
        brandCta.href = "#";
        brandCta.style.pointerEvents = "none";
        brandCta.style.cursor = "default";
      }
    }

    // Fetch Shopify products by SKU
    const criteoProducts = products.slice(0, 4);
    const uniqueSKUs = [...new Set(criteoProducts.map((p) => p.ProductId))];

    const shopifyProductsMap = (await this._getProducts(uniqueSKUs)).reduce(
      (map, product) => {
        const sku =
          product.tags.find((t) => t.startsWith("sku-"))?.replace("sku-", "") ||
          product.variants?.nodes?.[0]?.sku;
        if (sku) map[sku] = product;
        return map;
      },
      {},
    );

    const shopifyProducts = criteoProducts
      .map((criteoProduct) => {
        const shopifyProduct = shopifyProductsMap[criteoProduct.ProductId];
        return shopifyProduct
          ? { ...shopifyProduct, criteo: criteoProduct }
          : null;
      })
      .filter(Boolean);

    const productsZone = flagshipContainer.querySelector(
      this.#selectors.productsZone,
    );
    shopifyProducts
      .map((p) => this.#createProductCard(p))
      .forEach((card) => productsZone.appendChild(card));

    // Fallback CTA: first available product URL if no footer redirect URL set
    const firstAvailable =
      shopifyProducts.find((p) => p.availableForSale) || shopifyProducts[0];

    if (
      brandCta &&
      firstAvailable?.onlineStoreUrl &&
      brandCta.href.endsWith("#")
    ) {
      brandCta.href = firstAvailable.onlineStoreUrl;
      brandCta.style.pointerEvents = "";
      brandCta.style.cursor = "";
      brandCta.dataset.criteoOnClickBeacon = OnClickBeacon;
    }

    flagshipContainer.dataset.criteoPlacementViewBeacon = OnViewBeacon;
    flagshipContainer._onLoadBeacon = OnLoadBeacon;

    return flagshipContainer;
  }

  /**
   * Applies midBranding / bottomBranding behavior:
   * keeps only the first product card, sets brand CTA to first product URL,
   * and fires onLoad + onClick beacons directly (no clones).
   * @param {HTMLElement} flagshipElement - Already inserted in the DOM
   * @param {object} flagshipData
   */
  #applyPlacementBehavior(flagshipElement, flagshipData) {
    const { OnClickBeacon } = flagshipData;
    const flagship = flagshipElement.querySelector(
      this.#selectors.flagshipContainer,
    );
    const productsZone = flagship.querySelector(this.#selectors.productsZone);

    // Keep only first product card
    const allCards = Array.from(
      productsZone.querySelectorAll(this.#selectors.productCard),
    );
    allCards.forEach((card, i) => {
      if (i > 0) card.remove();
    });

    // Brand CTA URL = first product URL
    const firstProductLink = productsZone.querySelector(
      this.#selectors.productLink,
    );
    const brandCta = flagship.querySelector(this.#selectors.brandCta);
    if (firstProductLink && brandCta) {
      brandCta.href = firstProductLink.href;
      brandCta.dataset.criteoOnClickBeacon = OnClickBeacon;
      brandCta.style.pointerEvents = "";
      brandCta.style.cursor = "";
      this.#log("Brand CTA set to first product URL:", brandCta.href);
    }

    // onLoad beacons: flagship + visible product cards
    const visibleCards = Array.from(
      flagship.querySelectorAll(this.#selectors.productCard),
    );
    const loadBeacons = [
      flagshipElement._onLoadBeacon,
      ...visibleCards.map((c) => c.dataset.criteoOnLoadBeacon),
    ].filter(Boolean);
    this._onLoadEvent(loadBeacons);

    // onClick beacons: visible product links + brandCta
    const clickables = [
      ...visibleCards.map((c) => c.querySelector(this.#selectors.productLink)),
      brandCta,
    ].filter((el) => el?.dataset?.criteoOnClickBeacon);
    this._onClickEvent(clickables);
  }

  /**
   * Sets the background image based on container width and listens for breakpoint changes.
   * Must be called AFTER the element is inserted in the DOM (needs getBoundingClientRect).
   * @param {HTMLElement} flagshipElement
   * @param {object} rendering
   */
  #setupUpdateImage(flagshipElement, rendering) {
    if (!rendering) return;

    const brandImg = flagshipElement.querySelector(this.#selectors.brandImage);
    const container = flagshipElement.querySelector(
      this.#selectors.flagshipContainer,
    );
    if (!brandImg || !container) return;

    const updateImage = (mobile) => {
      const src = mobile
        ? rendering.mobile_background_image
        : rendering.desktop_background_image;
      const alt = mobile
        ? rendering.mobile_background_image_alt_text
        : rendering.desktop_background_image_alt_text;

      if (!src) return;
      const fullSrc = "https:" + src;
      if (brandImg.src === fullSrc) return;
      brandImg.src = fullSrc;
      brandImg.alt = alt || "Promoción";
    };

    // Initial image based on actual container width (not window.innerWidth)
    updateImage(container.getBoundingClientRect().width < 769);

    // ResizeObserver on the container itself — matches the container query breakpoint exactly
    const ro = new ResizeObserver((entries) => {
      if (!flagshipElement.isConnected) {
        ro.disconnect();
        return;
      }
      const width = entries[0].contentRect.width;
      updateImage(width < 769);
    });

    ro.observe(container);
  }

  /**
   * Inserts a flagship element into the grid based on placement type.
   * - midBranding / topBranding: after the second native row (2 * cols)
   * - bottomBranding: always appended at the end of the grid
   * @param {HTMLElement} grid
   * @param {HTMLElement} flagshipElement
   * @param {string} placementType
   */
  #insertPlacement(grid, flagshipElement, placementType) {
    if (placementType === CriteoFlagship.#PLACEMENT_TYPES.BOTTOM) {
      grid.appendChild(flagshipElement);
    } else {
      const cols = this.#getColumnsCount(grid);
      const nativeItems = Array.from(grid.children).filter(
        (el) => !el.id?.includes("criteo"),
      );
      const pos = 2 * cols;
      if (nativeItems[pos]) {
        grid.insertBefore(flagshipElement, nativeItems[pos]);
      } else {
        grid.appendChild(flagshipElement);
      }
    }
    this._sponsoredBadge(flagshipElement);
    this.#log(`Placement inserted: ${placementType}`);
  }

  /**
   * Detects the actual number of columns in the grid
   * @param {HTMLElement} grid
   * @returns {number}
   */
  #getColumnsCount(grid) {
    const isListMode = grid.closest("#snize-search-results-list-mode") !== null;
    if (isListMode) return 1;

    const nativeItems = Array.from(
      grid.querySelectorAll(".snize-product"),
    ).filter((el) => !el.id?.includes("criteo"));

    if (nativeItems.length >= 2) {
      const firstTop = nativeItems[0].offsetTop;
      let cols = 1;
      for (let i = 1; i < nativeItems.length; i++) {
        if (nativeItems[i].offsetTop !== firstTop) break;
        cols++;
      }
      if (cols > 1) return cols;
    }

    // Fallback to SCSS breakpoints
    const width = window.innerWidth;
    if (width >= 1300) return 4;
    if (width >= 969) return 3;
    if (width >= 751) return 2;
    return 1;
  }

  /**
   * Sets up resize and grid/list toggle observers.
   * On column count change: removes all flagship wrappers and resets state
   * so the next exec() call re-renders from scratch.
   * bottomBranding always stays at the end — no reposition needed on resize.
   * @param {HTMLElement} grid
   */
  #setupResizeObserver(grid) {
    let resizeTimer;
    let currentCols = this.#getColumnsCount(grid);

    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const newCols = this.#getColumnsCount(grid);
        if (newCols === currentCols) return;
        this.#log(`Columns changed: ${currentCols} → ${newCols}, re-rendering`);
        currentCols = newCols;
        document
          .querySelectorAll("#criteo-flagship-wrapper")
          .forEach((el) => el.remove());
        this.#hasRendered = false;
        this.#flagshipElement = null;
        window.removeEventListener("resize", onResize);
      }, 500);
    };

    window.addEventListener("resize", onResize, { passive: true });

    // MutationObserver: detect grid ↔ list mode toggle
    if (this.#layoutObserver) {
      this.#layoutObserver.disconnect();
      this.#layoutObserver = null;
    }

    const snizeContainer = grid.closest(
      "#snize-search-results-grid-mode, #snize-search-results-list-mode",
    );

    if (snizeContainer) {
      const initialId = snizeContainer.id;
      this.#layoutObserver = new MutationObserver(() => {
        if (snizeContainer.id === initialId) return;
        this.#log(
          `Layout toggle detected: ${initialId} → ${snizeContainer.id}`,
        );
        this.#layoutObserver.disconnect();
        this.#layoutObserver = null;
        window.removeEventListener("resize", onResize);
        document
          .querySelectorAll("#criteo-flagship-wrapper")
          .forEach((el) => el.remove());
        this.#flagshipElement = null;
      });
      this.#layoutObserver.observe(snizeContainer, {
        attributes: true,
        attributeFilter: ["id"],
      });
    }
  }

  /**
   * Main execution method.
   * Fetches the Criteo API once, then independently processes each
   * placement type (midBranding, bottomBranding, topBranding) that appears
   * in the response. Each placement is rendered and inserted directly —
   * no clones needed.
   * @param {"search-results-page"|"collections"|"products"} template
   */
  async exec(template) {
    try {
      if (!CriteoFlagship.isEnabled) return;
      if (!this._isActive) return;

      const existing = document.querySelectorAll("#criteo-flagship-wrapper");
      if (existing.length > 0 && this.#response) {
        this.#log("Flagship already in DOM, skipping re-render");
        return;
      }

      document
        .querySelectorAll("#criteo-flagship-wrapper")
        .forEach((el) => el.remove());

      // Fetch API response only once (reuse cached if available)
      if (!this.#response) {
        const gridForParams =
          template === "products" ? null : await this.#grid();
        const params = await this.#generateParams(template, gridForParams);
        const eventType =
          template === "collections"
            ? "viewCategory"
            : template === "products"
              ? "viewItem"
              : "viewSearchResult";
        this.#response = await this._baseRequest(eventType, params);
      }

      const grid = template === "products" ? null : await this.#grid();

      const eventType =
        template === "collections"
          ? "viewCategory"
          : template === "products"
            ? "viewItem"
            : "viewSearchResult";

      this.#log("Event type:", eventType);
      this.#log("Base URL:", window.mmkt?.config?.criteoAPI?.baseURL);
      this.#log("Partner ID:", window.mmkt?.config?.criteoAPI?.partnerId);
      this.#log("Full API response:", this.#response);

      const { placements } = this.#response;

      if (!placements?.length) {
        this.#log("No placements available in response");
        return;
      }

      this.#log("Total placements found:", placements.length);

      const device = screen.width <= 750 ? "Mobile" : "Desktop";
      const deviceLower = device.toLowerCase();
      const viewType =
        template === "collections"
          ? "Category"
          : template === "products"
            ? "Item"
            : "SearchResult";

      // Map each placement type to its possible API key variants
      const placementKeyMap = {
        [CriteoFlagship.#PLACEMENT_TYPES.MID]: [
          `view${viewType}Api${device}-midBranding`,
          `view${viewType}Api${deviceLower}-midBranding`,
        ],
        [CriteoFlagship.#PLACEMENT_TYPES.BOTTOM]: [
          `view${viewType}Api${device}-bottomBranding`,
          `view${viewType}Api${deviceLower}-bottomBranding`,
        ],
        ...(template === "products" && {
          [CriteoFlagship.#PLACEMENT_TYPES.TOP]: [
            `view${viewType}Api${device}-topBranding`,
            `view${viewType}Api${deviceLower}-topBranding`,
          ],
        }),
      };

      this.#log(
        "Available placement keys in response:",
        placements.map((p) => Object.keys(p)),
      );

      let hasCollectionPlacements = false;

      for (const [type, keys] of Object.entries(placementKeyMap)) {
        const matchedPlacement = placements.find((p) => keys.some((k) => p[k]));
        if (!matchedPlacement) continue;

        const matchedKey = keys.find((k) => matchedPlacement[k]);
        const flagshipData = matchedPlacement[matchedKey]?.[0];
        if (!flagshipData) continue;

        this.#log(`Processing placement: ${type}`, flagshipData);

        if (template === "products") {
          const anchor = document.querySelector(
            ".product-description-navigation--container",
          );
          if (!anchor) continue;

          const flagshipElement = await this.#renderFlagship(
            flagshipData,
            grid,
          );
          flagshipElement.classList.add("criteo-flagship--pdp");
          anchor.insertAdjacentElement("afterend", flagshipElement);
          this._sponsoredBadge(flagshipElement);

          this.#applyPlacementBehavior(flagshipElement, flagshipData);
          this.#setupUpdateImage(flagshipElement, flagshipData.rendering);

          const pdpCards = Array.from(
            flagshipElement.querySelectorAll(this.#selectors.productCard),
          );
          this._viewPortEvent([flagshipElement, ...pdpCards]);
        } else {
          const flagshipElement = await this.#renderFlagship(
            flagshipData,
            grid,
          );
          this.#insertPlacement(grid, flagshipElement, type);
          this.#applyPlacementBehavior(flagshipElement, flagshipData);
          this.#setupUpdateImage(flagshipElement, flagshipData.rendering);

          const cards = Array.from(
            flagshipElement.querySelectorAll(this.#selectors.productCard),
          );
          this._viewPortEvent([flagshipElement, ...cards]);

          hasCollectionPlacements = true;
        }
      }

      if (hasCollectionPlacements && grid) {
        this.#setupResizeObserver(grid);
      }

      this.#log("All placements rendered successfully");
    } catch (error) {
      this.#log("Error in exec():", error);
      this.#response = null;
      this.#hasRendered = false;
      this.#flagshipElement = null;
    }
  }

  reset() {
    this.#response = null;
    this.#hasRendered = false;
    this.#flagshipElement = null;
  }
}
